<template>
	<section class="pc-recommend" v-if="partOneItems.length >= 2 && podcastTopSix.length >= 5">
		<div class="title">
			{{ t("square.recommend.title") }}
			<div class="more" @click="handleMore">{{ t("square.recommend.more") }}</div>
		</div>
		<div class="part-one">
			<div
				v-for="(item, index) in partOneItems"
				:key="item.id || index"
				class="left"
				:ref="el => bindPodcastCardRef(el, item, index)"
				@click="handleToSquareDetail(item, index)"
			>
				<div class="left-thumb">
					<img :src="item.cover" alt="" />
					<div class="top-gradient"></div>
					<div class="left-duration" v-if="item.media_second_str || item.media_second != null">
						{{ item.media_second_str || formatTime(item.media_second) }}
					</div>
				</div>
				<div class="left-body">
					<img class="left-avatar" :src="item.avatar" alt="" />
					<div class="left-text">
						<div class="left-title">{{ item.title }}</div>
						<div class="left-meta">
							<span class="left-nickname">{{ item.nickname }}</span>
							<span class="left-views">
								<img src="../../../images/eye.png" alt="" class="left-eye" />
								{{ formatFeaturedViewCount(item.view_count) }}
							</span>
						</div>
					</div>
				</div>
			</div>
		</div>
		<div class="part-two">
			<div class="box box1">
				<div
					class="content"
					v-for="(item, index) in box1"
					:key="item.id"
					:ref="el => bindPodcastCardRef(el, item, index + 2)"
					@click="handleToSquareDetail(item, index + 2)"
				>
					<div class="pic">
						<img :src="item.cover" alt="" />
						<img
							src="../../../images/vedio.png"
							alt=""
							class="vedio"
							v-if="item.type == 1"
							@click.stop="handleVideoPlayClick(item, index + 2)"
						/>
						<div class="mask" v-if="item.type == 1"></div>
						<div class="time" v-if="item.type == 1">
							{{ formatTime(item.media_second) }}
						</div>
					</div>
					<div class="juzi">
						<p class="juzi1">
							{{ item.title }}
						</p>
						<div class="juzi-meta">
							<span class="juzi-views">
								<img src="../../../images/eye.png" alt="" class="juzi-eye" />
								{{ formatFeaturedViewCount(item.view_count) }}
							</span>
						</div>
					</div>
				</div>
			</div>
			<div class="box">
				<div
					class="content"
					v-for="(item, index) in box2"
					:key="item.id"
					:ref="el => bindPodcastCardRef(el, item, index + 4)"
					@click="handleToSquareDetail(item, index + 4)"
				>
					<div class="pic">
						<img :src="item.cover" alt="" />
						<img
							src="../../../images/vedio.png"
							alt=""
							class="vedio"
							v-if="item.type == 1"
							@click.stop="handleVideoPlayClick(item, index + 4)"
						/>
						<div class="mask" v-if="item.type == 1"></div>
						<div class="time" v-if="item.type == 1">
							{{ formatTime(item.media_second) }}
						</div>
					</div>
					<div class="juzi">
						<p class="juzi1">
							{{ item.title }}
						</p>
						<div class="juzi-meta">
							<span class="juzi-views">
								<img src="../../../images/eye.png" alt="" class="juzi-eye" />
								{{ formatFeaturedViewCount(item.view_count) }}
							</span>
						</div>
					</div>
				</div>
			</div>
		</div>
	</section>
</template>

<script setup>
import { computed, inject, onActivated } from "vue";
import { useSquarePcPageData } from "@variants/seer/square/composables/useSquarePageData";
import { useI18n } from "~/composables/useI18n";
import {
	buildContentParams,
	MEDIA_TYPES,
	useMediaCardImpression,
} from "../../../composables/useMediaSquareTracking";

const { t, locale } = useI18n();
const pageData = useSquarePcPageData();

// 注入父组件提供的方法
const formatTime = inject("formatTime");
const toSquareDetail = inject("toSquareDetail");
const mediaTracking = inject("mediaSquareTracking", null);

const { bindCardRef: bindPodcastCardRef } = useMediaCardImpression({
	track: (eventName, params) => mediaTracking?.track(eventName, params),
	impressionEvent: "podcast_card_impression",
	getItemParams: (item, index) =>
		buildContentParams(item, {
			position: index,
			formatTime,
			authorFallback: t("square.podcast.defaultAuthor"),
		}),
});

const handleToSquareDetail = (item, index) => {
	if (!item) return;
	mediaTracking?.trackPodcastCardClick(item, index, formatTime, t("square.podcast.defaultAuthor"));
	toSquareDetail(item);
};

const handleVideoPlayClick = (item, index) => {
	if (!item) return;
	mediaTracking?.trackVideoPlayClick(item, {
		mediaType: MEDIA_TYPES.PODCAST,
		position: index,
		formatTime,
		authorFallback: t("square.podcast.defaultAuthor"),
	});
	toSquareDetail(item);
};

const handleMore = () => {
	mediaTracking?.trackMediaMoreClick({
		sectionId: "recommend",
		mediaType: MEDIA_TYPES.PODCAST,
		currentCount: partOneItems.value.length + box1.value.length + box2.value.length,
	});
	navigateTo("/seer/square/podcast");
};

const formatFeaturedViewCount = count => {
	const value = Number(count) || 0;
	if (value >= 10000) {
		if (locale.value === "en") {
			return t("square.views.count", { count: `${(value / 1000).toFixed(1)}K` });
		}
		return t("square.views.tenThousand", { count: (value / 10000).toFixed(1) });
	}
	return t("square.views.count", { count: value });
};

const recommendData = computed(
	() => pageData.value?.recommend ?? { featuredLeft: null, podcastTopSix: [] }
);

onActivated(() => {
	refreshNuxtData("square-pc-index-data");
});

const featuredLeft = computed(() => recommendData.value?.featuredLeft ?? null);
const podcastTopSix = computed(() => recommendData.value?.podcastTopSix ?? []);

/** 顶部两列大卡：精选 + 列表首条（去重） */
const partOneItems = computed(() => {
	const items = [];
	if (featuredLeft.value) items.push(featuredLeft.value);
	for (const item of podcastTopSix.value) {
		if (items.length >= 2) break;
		if (item?.id != null && items.some(curr => curr?.id === item.id)) continue;
		items.push(item);
	}
	return items;
});

const box1 = computed(() => podcastTopSix.value.slice(1, 3));
const box2 = computed(() => podcastTopSix.value.slice(3, 5));
</script>

<style lang="scss" scoped>
@mixin top-blur {
	position: absolute;
	top: 0;
	left: 0;
	width: 100%;
	height: 30%;
	background: linear-gradient(to bottom, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0) 100%);
	pointer-events: none;
}

@mixin huanhang {
	// 设置宽度
	display: -webkit-box; /* 使用旧版 Flexbox 模型（关键） */
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
	text-overflow: ellipsis;
	line-height: 1.5;
}

.pc-recommend {
	display: flex;
	flex-direction: column;
	justify-content: space-between;
	width: 100%;
	margin: 30px 0 100px;
	box-sizing: border-box;
	border-radius: 20px;
	box-shadow: 0 4px 24.6px 0 rgba(0, 0, 0, 0.1);
	padding: 30px;

	.title {
		display: flex;
		justify-content: space-between;
		align-items: center;
		font-size: 28px;
		color: #333333;
		font-weight: bold;
		margin-bottom: 20px;

		.more {
			font-size: 18px;
			color: #999999;
			padding: 3px 30px;
			font-weight: normal;
			border-radius: 8px;
			border: 1px solid #dfdfdf;
			cursor: pointer;
		}
	}

	.part-one {
		display: flex;
		align-items: stretch;
		gap: 25px;
		width: 100%;
		margin-bottom: 40px;

		.left {
			display: flex;
			flex-direction: column;
			flex: 1 1 0;
			min-width: 0;
			border-radius: 10px;
			overflow: hidden;
			cursor: pointer;
			background: #ffffff;
			border: 1px solid #ececec;
			box-sizing: border-box;

			.left-thumb {
				position: relative;
				flex-shrink: 0;
				width: 100%;
				aspect-ratio: 16 / 9;
				overflow: hidden;

				> img {
					position: absolute;
					inset: 0;
					width: 100%;
					height: 100%;
					object-fit: cover;
					object-position: center;
					display: block;
				}

				.top-gradient {
					@include top-blur;
				}

				.left-duration {
					position: absolute;
					bottom: 10px;
					right: 12px;
					padding: 2px 8px;
					font-size: 14px;
					color: #ffffff;
					background: rgba(0, 0, 0, 0.72);
					border-radius: 4px;
					line-height: 1.4;
					z-index: 1;
				}
			}

			.left-body {
				display: flex;
				flex-direction: row;
				align-items: flex-start;
				flex: 1;
				box-sizing: border-box;
				gap: 12px;
				padding: 16px 20px 18px;
				color: #333333;
			}

			.left-avatar {
				width: 48px;
				height: 48px;
				border-radius: 50%;
				object-fit: cover;
				flex-shrink: 0;
			}

			.left-text {
				flex: 1;
				min-width: 0;
			}

			.left-title {
				font-size: 18px;
				font-weight: 500;
				color: #333333;
				line-height: 1.5;
				margin-bottom: 10px;
				@include huanhang;
			}

			.left-meta {
				display: flex;
				flex-direction: row;
				align-items: center;
				justify-content: left;
				gap: 16px;
				font-size: 14px;
			}

			.left-nickname {
				color: #333333;
				font-weight: 600;
				overflow: hidden;
				text-overflow: ellipsis;
				white-space: nowrap;
				min-width: 0;
			}

			.left-views {
				display: inline-flex;
				align-items: center;
				gap: 6px;
				flex-shrink: 0;
				color: #a0a0a0;
			}

			.left-eye {
				width: 16px;
				height: 16px;
				object-fit: contain;
				flex-shrink: 0;
			}
		}
	}

	.part-two {
		width: 100%;
		background-color: #ffffff;
		border-radius: 10px;
		border: 1px solid #efefef;
		box-sizing: border-box;

		.box {
			display: flex;
			padding: 18px 0;
			margin: 0 20px;
			gap: 13px;

			.content {
				display: flex;
				align-items: center;
				flex: 0 0 50%;
				max-width: 50%;
				min-width: 0;
				cursor: pointer;

				.pic {
					position: relative;
					width: 176px;
					height: 99px;
					margin-right: 20px;
					border-radius: 10px;
					overflow: hidden;

					&:hover {
						.vedio,
						.mask,
						.time {
							opacity: 0;
						}
					}

					img {
						width: 100%;
						height: 100%;
						object-fit: cover;
					}

					.vedio {
						opacity: 1;
						position: absolute;
						top: 5px;
						right: 10px;
						width: 10%;
						height: auto;
						transition: opacity 0.3s ease;
					}

					.mask {
						opacity: 1;
						position: absolute;
						width: 100%;
						height: 100%;
						top: 0;
						background: linear-gradient(
							to bottom,
							rgba(0, 0, 0, 0) 0%,
							rgba(0, 0, 0, 0) 20%,
							rgba(0, 0, 0, 0) 65%,
							rgba(0, 0, 0, 0.2) 100%
						);
						pointer-events: none;
						transition: opacity 0.3s ease;
					}

					.time {
						opacity: 1;
						position: absolute;
						bottom: 8px;
						right: 10px;
						font-size: 14px;
						color: #ffffff;
						transition: opacity 0.3s ease;
					}
				}

				.juzi {
					font-size: 18px;
					height: 99px;
					display: flex;
					flex-direction: column;
					flex: 1;
					min-width: 0;
					gap: 10px;
					padding-right: 20px;

					.juzi1 {
						font-size: 18px;
						font-weight: 500;
						color: #333;
						margin: 0;
						width: 100%;
						@include huanhang;
					}

					.juzi-meta {
						display: flex;
						flex-direction: row;
						align-items: center;
						gap: 16px;
						font-size: 14px;
						width: 100%;
					}
					.juzi-views {
						display: inline-flex;
						align-items: center;
						gap: 6px;
						flex-shrink: 0;
						color: #a0a0a0;
					}

					.juzi-eye {
						width: 16px;
						height: 16px;
						object-fit: contain;
						flex-shrink: 0;
					}
				}
			}
		}

		.box1 {
			border-bottom: 1px solid #efefef;
		}
	}
}
</style>
